/**
 * What a plan's seats are doing, projected from `GET /customer/seat-usage`.
 *
 * The response's field names do not mean what they look like, and two of them are traps:
 *
 * · **`usersAmount` is the CAP, not a count.** `BuildSeatUsageResponse` sets it from the
 *   entitlement's `ActiveClientLimit`.
 * · **On an unlimited plan there is no cap at all**, and the backend fakes one so that older
 *   clients computing `used = usersAmount − freeSeats` still get the right occupancy: `freeSeats`
 *   becomes a flat **999** and `usersAmount` becomes `active + pending + 999`. Drawing one cell per
 *   seat from those numbers would put a thousand cells in a card. `seatSegments` is therefore
 *   empty when the plan is unlimited, and the count is stated in words instead.
 * · **Occupancy is active PLUS pending.** An outstanding invitation holds a seat —
 *   `EnforceInvitationSeatLimitAsync` counts both — so a bar drawn from `activeCount` alone would
 *   tell a trainer at the cap that they have room.
 *
 * The free plan's cap is **1**, not the 200 the endpoint's own XML comment claims; the seeded plans
 * are 1 / 5 / unlimited. So a one-cell bar is a real state, not an edge case.
 *
 * ── There is ONE bar projection now, and there were two ──────────────────────────────────────
 * `seatUsage().cells` — a per-seat array for the invite ticket, marking the seat an invitation had
 * just consumed — was deleted on 21 September 2026 with the ticket's seat card. The owner's C2
 * direction replaced that card with a sentence in the dialog's footer, and a projection with no
 * reader is the dead code the house rule names. `seatSegments` for `/konto`'s plate is unaffected
 * and keeps `MAX_DRAWN_SEATS`, which was never only the ticket's.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

import type { TrainWithMeModulesClientsDtosResponsesSeatUsageResponse as SeatUsageDto } from '~/shared/api/generated/model';

/**
 * One cell of the SUBSCRIPTION plate's bar, which tells the two kinds of occupant apart.
 *
 * On `/konto` the question is "what is my plan carrying", and the canon draws a hatched segment and
 * a legend entry for the offers nobody has accepted yet. The invite dialog asks a different
 * question — "is there room" — and answers it in words, so it draws no bar at all.
 */
export type SeatSegment = 'active' | 'pending' | 'free';

export interface SeatUsage {
  /** Coaching relationships that are live. */
  readonly active: number;
  /** Offers nobody has accepted yet. Each holds a seat — see the header. */
  readonly pending: number;
  /** Active clients plus outstanding invitations. Both hold a seat. */
  readonly taken: number;
  /** `null` on an unlimited plan — there is no number to show. */
  readonly cap: number | null;
  readonly free: number | null;
  readonly unlimited: boolean;
  /** Whether the form may be submitted at all. */
  readonly canInvite: boolean;
}

/**
 * Above this the bar stops being a bar and becomes a texture, so the numbers carry it alone. The
 * shipped plans cap at 5, so this only ever engages on a plan that does not exist yet — it is a
 * floor under a future entitlement, not a guess about this one.
 */
const MAX_DRAWN_SEATS = 24;

/**
 * The seat usage, or `null` while the read is in flight.
 *
 * `extraTaken` is the seat a write has consumed that the response does not know about yet. The
 * confirmation drew the seat count straight off the cached read, which is the state BEFORE the
 * invitation — so a trainer who had just filled their fourth of five seats was congratulated over a
 * count of three, and it corrected itself a moment later when the invalidated read landed. The
 * caller freezes the projection at the moment of success, so the correction cannot double-count.
 */
export function seatUsage(
  dto: SeatUsageDto | null | undefined,
  options: { readonly extraTaken?: number } = {},
): SeatUsage | null {
  if (dto == null) return null;

  const active = dto.activeCount ?? 0;
  // The seat a write has consumed is an OFFER nobody has accepted — that is what inviting does — so
  // it joins the pending count rather than the active one, and `active + pending` stays `taken`.
  const pending = (dto.pendingCount ?? 0) + (options.extraTaken ?? 0);
  const taken = active + pending;
  const unlimited = dto.isUnlimited === true;

  if (unlimited) {
    return {
      active,
      pending,
      taken,
      cap: null,
      free: null,
      unlimited: true,
      canInvite: true,
    };
  }

  const cap = typeof dto.usersAmount === 'number' ? dto.usersAmount : taken;
  // Recomputed rather than read off `freeSeats`, so every reading of these seats comes from the
  // same two numbers and none of them can disagree with another.
  const free = Math.max(0, cap - taken);

  return {
    active,
    pending,
    taken,
    cap,
    free,
    unlimited: false,
    canInvite: free > 0,
  };
}

/**
 * The bar `/konto`'s subscription plate draws, told apart by KIND — see `SeatSegment`.
 *
 * An unlimited plan draws nothing, and neither does a cap past `MAX_DRAWN_SEATS`. A surplus taken
 * seat fills the bar without growing it, which is the case a stale read produces and the reason the
 * loop runs over the CAP.
 */
export function seatSegments(seats: SeatUsage): readonly SeatSegment[] {
  const cap = seats.cap;
  if (seats.unlimited || cap === null || cap > MAX_DRAWN_SEATS) return [];
  return Array.from({ length: cap }, (_, index) =>
    index < seats.active ? 'active' : index < seats.taken ? 'pending' : 'free',
  );
}
